/**
 * Geometry of line shapes: arrowheads and other end caps, optional bend.
 * A line runs through the middle of its box from the left to the right edge
 * (the box rotation turns it); caps are sized by the stroke width, so they
 * scale with it and stay plain vector paths (screen, PNG, PDF and print).
 */

/** End caps of a line, in the order of the picker. */
export const LINE_ENDS = ["none", "arrow", "triangle", "circle", "diamond", "bar"] as const;
export type LineEnd = (typeof LINE_ENDS)[number];

export const LINE_END_LABELS: Record<LineEnd, string> = {
  none: "Keine",
  arrow: "Pfeil",
  triangle: "Dreieck",
  circle: "Kreis",
  diamond: "Raute",
  bar: "Balken",
};

type Point = { x: number; y: number };

/** One cap: path data plus whether it is filled (otherwise stroked like the line). */
export type LineCapPath = { d: string; filled: boolean };

export type LineGeometry = {
  /** Path data of the line itself (shortened where a cap covers its end). */
  d: string;
  caps: LineCapPath[];
};

const round = (value: number) => Math.round(value * 100) / 100;
const fmt = (point: Point) => `${round(point.x)} ${round(point.y)}`;

/** Length of a cap along the line for a stroke width (grows with the stroke). */
export function lineCapSize(strokeWidth: number): number {
  return strokeWidth * 3 + 4;
}

/**
 * Cap at `tip`, pointing along the unit vector `dir` (from the line towards
 * the tip). `inset` is how far the line has to stop before the tip so that
 * its end hides under the cap.
 */
export function lineCapGeometry(
  kind: LineEnd,
  tip: Point,
  dir: Point,
  strokeWidth: number,
): { path: LineCapPath | null; inset: number } {
  if (kind === "none") return { path: null, inset: 0 };
  const size = lineCapSize(strokeWidth);
  const normal = { x: -dir.y, y: dir.x };
  const at = (along: number, across: number): Point => ({
    x: tip.x - dir.x * along + normal.x * across,
    y: tip.y - dir.y * along + normal.y * across,
  });
  switch (kind) {
    case "arrow": {
      // Open chevron, stroked; pulled back a little so the round join ends at the tip.
      const back = strokeWidth / 2;
      const a = at(size + back, size * 0.6);
      const t = at(back, 0);
      const b = at(size + back, -size * 0.6);
      return { path: { d: `M${fmt(a)} L${fmt(t)} L${fmt(b)}`, filled: false }, inset: back };
    }
    case "triangle": {
      const half = size * 0.55;
      const d = `M${fmt(tip)} L${fmt(at(size, half))} L${fmt(at(size, -half))} Z`;
      return { path: { d, filled: true }, inset: size * 0.8 };
    }
    case "circle": {
      const r = size * 0.42;
      const c = at(r, 0);
      const d = `M${fmt({ x: c.x - r, y: c.y })} A${round(r)} ${round(r)} 0 1 0 ${fmt({ x: c.x + r, y: c.y })} A${round(r)} ${round(r)} 0 1 0 ${fmt({ x: c.x - r, y: c.y })} Z`;
      return { path: { d, filled: true }, inset: r };
    }
    case "diamond": {
      const half = size * 0.45;
      const d = `M${fmt(tip)} L${fmt(at(size / 2, half))} L${fmt(at(size, 0))} L${fmt(at(size / 2, -half))} Z`;
      return { path: { d, filled: true }, inset: size / 2 };
    }
    case "bar": {
      const half = size * 0.6;
      const back = strokeWidth / 2;
      return {
        path: { d: `M${fmt(at(back, half))} L${fmt(at(back, -half))}`, filled: false },
        inset: 0,
      };
    }
  }
}

function unit(from: Point, to: Point): Point {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const length = Math.hypot(dx, dy) || 1;
  return { x: dx / length, y: dy / length };
}

function moveTowards(point: Point, dir: Point, distance: number): Point {
  return { x: point.x - dir.x * distance, y: point.y - dir.y * distance };
}

/**
 * Line through the middle of a `width` × `height` box with caps. `curve`
 * bends it: the control point sits `curve × width` above (negative: below)
 * the middle, so the peak is half of that.
 */
export function lineGeometry(input: {
  width: number;
  height: number;
  strokeWidth: number;
  start?: LineEnd | undefined;
  end?: LineEnd | undefined;
  curve?: number | undefined;
}): LineGeometry {
  const { width: w, height: h, strokeWidth } = input;
  const p0 = { x: 0, y: h / 2 };
  const p2 = { x: w, y: h / 2 };
  const bend = input.curve ?? 0;
  const control = bend ? { x: w / 2, y: h / 2 - bend * w } : null;
  // Directions pointing outwards at both ends (tangents of the curve).
  const startDir = unit(control ?? p2, p0);
  const endDir = unit(control ?? p0, p2);
  const maxInset = w / 2;
  const startCap = lineCapGeometry(input.start ?? "none", p0, startDir, strokeWidth);
  const endCap = lineCapGeometry(input.end ?? "none", p2, endDir, strokeWidth);
  const a = moveTowards(p0, startDir, Math.min(startCap.inset, maxInset));
  const b = moveTowards(p2, endDir, Math.min(endCap.inset, maxInset));
  const d = control ? `M${fmt(a)} Q${fmt(control)} ${fmt(b)}` : `M${fmt(a)} L${fmt(b)}`;
  const caps = [startCap.path, endCap.path].filter((cap): cap is LineCapPath => cap !== null);
  return { d, caps };
}
